import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import type { IIssuerByTickerSymbolQuery } from "lib/queries/__generated__/IssuerByTickerSymbolQuery.generated";
import {
  IAssetType,
  IIndicatorDataSource,
  IIndicatorVertical,
  IPlatformVertical,
  ISecurityCountry,
} from "types/graphqlTypes";

export default function getPlatformVerticalByIssuer(
  issuer:
    | IAmericanCompanyIssuerTopLevelFragment
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment,
  indicatorDataSource?: IIndicatorDataSource,
): IPlatformVertical {
  if (issuer.__typename === "AmericanCompanyIssuer") {
    return issuer.isREIT ? IPlatformVertical.Reits : IPlatformVertical.Stocks;
  }

  if (issuer.__typename === "BrazilianCompanyIssuer") {
    if (indicatorDataSource === IIndicatorDataSource.Morningstar) {
      return IPlatformVertical.Stocks;
    }
    return IPlatformVertical.Acoes;
  }

  return IPlatformVertical.Fiis;
}

export function getPlatformVerticalTitle(platformVertical: IPlatformVertical): string {
  if (platformVertical === IPlatformVertical.Stocks) {
    return "Stocks";
  }
  if (platformVertical === IPlatformVertical.Reits) {
    return "REITs";
  }
  if (platformVertical === IPlatformVertical.Acoes) {
    return "Ações";
  }
  if (platformVertical === IPlatformVertical.Fiis) {
    return "FIIs";
  }

  throw new Error(`Invalid vertical: ${platformVertical}`);
}

export function platformVerticalFromSlug(slug: string): IPlatformVertical | null {
  if (slug === "stocks") {
    return IPlatformVertical.Stocks;
  }
  if (slug === "reits") {
    return IPlatformVertical.Reits;
  }
  if (slug === "acoes") {
    return IPlatformVertical.Acoes;
  }
  if (slug === "fiis") {
    return IPlatformVertical.Fiis;
  }

  return null;
}

export function platformVerticalToSlug(platformVertical: IPlatformVertical): string {
  if (platformVertical === IPlatformVertical.Stocks) {
    return "stocks";
  }
  if (platformVertical === IPlatformVertical.Reits) {
    return "reits";
  }
  if (platformVertical === IPlatformVertical.Acoes) {
    return "acoes";
  }
  if (platformVertical === IPlatformVertical.Fiis) {
    return "fiis";
  }

  throw new Error(`Invalid vertical: ${platformVertical}`);
}

export function platformVerticalToSecurityCountry(platformVertical: IPlatformVertical): ISecurityCountry {
  if (platformVertical === IPlatformVertical.Stocks) {
    return ISecurityCountry.Us;
  }
  if (platformVertical === IPlatformVertical.Reits) {
    return ISecurityCountry.Us;
  }
  if (platformVertical === IPlatformVertical.Acoes) {
    return ISecurityCountry.Br;
  }
  if (platformVertical === IPlatformVertical.Fiis) {
    return ISecurityCountry.Br;
  }

  throw new Error(`Invalid vertical: ${platformVertical}`);
}

export function getIdealDataSourceFromPlatformVertical(platformVertical: IPlatformVertical): IIndicatorDataSource {
  if (platformVertical === IPlatformVertical.Stocks) {
    return IIndicatorDataSource.Morningstar;
  }
  if (platformVertical === IPlatformVertical.Reits) {
    return IIndicatorDataSource.Morningstar;
  }
  if (platformVertical === IPlatformVertical.Acoes) {
    return IIndicatorDataSource.Cvm;
  }
  if (platformVertical === IPlatformVertical.Fiis) {
    return IIndicatorDataSource.Fnet;
  }

  throw new Error(`Invalid vertical: ${platformVertical}`);
}

export function getVerticalsFromPlatformVertical(
  platformVertical: IPlatformVertical,
  indicatorDataSource?: IIndicatorDataSource,
): IIndicatorVertical[] {
  const verticals = [IIndicatorVertical.All];

  if (platformVertical === IPlatformVertical.Stocks || platformVertical === IPlatformVertical.Reits) {
    verticals.push(IIndicatorVertical.StocksAndReits);

    return [
      ...verticals,
      IIndicatorVertical.StocksAndReits,
      platformVertical === IPlatformVertical.Reits ? IIndicatorVertical.Reits : IIndicatorVertical.Stocks,
    ];
  }

  if (platformVertical === IPlatformVertical.Acoes) {
    if (indicatorDataSource === IIndicatorDataSource.Morningstar) {
      return [...verticals, IIndicatorVertical.StocksAndReits, IIndicatorVertical.Stocks];
    }

    return [...verticals, IIndicatorVertical.Acoes];
  }

  return [...verticals, IIndicatorVertical.Fiis];
}

export function overrideVerticalForDataSource(
  platformVertical: IPlatformVertical,
  dataSource: IIndicatorDataSource,
): IPlatformVertical {
  // Caso o usuário tenha selecionado Ações como vertical principal, mas quer usar os dados da Morningstar, devemos
  // alterar a vertical para Stocks. Do contrário mantém a vertical já selecionada ("ideal")
  if (platformVertical === IPlatformVertical.Acoes && dataSource === IIndicatorDataSource.Morningstar) {
    return IPlatformVertical.Stocks;
  }

  return platformVertical;
}

export type RootIssuer = NonNullable<
  Exclude<IIssuerByTickerSymbolQuery["issuerByTickerSymbol"], { __typename: "BrazilianRealEstateFundIssuer" }>
>;

export function getAssetType(issuer: RootIssuer) {
  if (issuer.__typename === "AmericanCompanyIssuer") {
    return issuer.isREIT ? IAssetType.AmericanReit : IAssetType.AmericanCompany;
  }
  if (issuer.__typename === "BrazilianCompanyIssuer") {
    return IAssetType.BrazilianCompany;
  }
  return IAssetType.BrazilianRealEstateFund;
}
